import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Building2, ChevronDown, ChevronRight, Loader2, MoreHorizontal, Search } from "lucide-react";
import type { Brand } from "@shared/schema";
import { CUSTOMER_EVIDENCE_FLAG } from "@shared/customer-evidence";
import { NanoBananaVersionControl } from "@/components/admin/nano-banana-version-control";
import { AdsExpressNativeInstructions } from "@/components/admin/ads-express-native-instructions";

type ImageEngineOverrideRow = {
  brandId: string;
  engine: string;
  updatedAt: string | null;
};

type BrandSettingDefinition = {
  key: string;
  name: string;
  description: string;
  defaultEnabled: boolean;
  requiresUserGrant: boolean;
};
type BrandSettingValue = {
  brandId: string;
  settingKey: string;
  enabled: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
};
type BrandSettingsResponse = {
  settings: BrandSettingDefinition[];
  values: BrandSettingValue[];
};

const GROUP_ORDER = [
  "Prompting and generation",
  "Rendering and safety",
  "Image editing",
  "Collaboration",
  "Other",
];
const FEATURE_GROUPS: Record<string, string> = {
  ads_express_image_engine_selector: "Rendering and safety",
  reference_images_enabled: "Prompting and generation",
  variant_aware_ads_enabled: "Prompting and generation",
  beta_ad_prompts_enabled: "Prompting and generation",
  brand_color_adherence: "Prompting and generation",
  brand_cta_style: "Prompting and generation",
  product_reference_library_enabled: "Prompting and generation",
  cross_brand_ad_templates_enabled: "Prompting and generation",
  ads_persona_character_appearance: "Prompting and generation",
  ads_persona_character_selector: "Prompting and generation",
  ads_persona_character_reference_sheet: "Prompting and generation",
  ads_copy_diversification: "Prompting and generation",
  diversify_mode: "Prompting and generation",
  exact_logo_overlay: "Rendering and safety",
  safe_zone_enhanced_render: "Rendering and safety",
  safe_zone_seam_blend: "Rendering and safety",
  safe_zone_person_seam_guard: "Rendering and safety",
  safe_zone_direct_916_guard: "Rendering and safety",
  safe_zone_portrait_continuation: "Rendering and safety",
  safe_zone_reframe: "Rendering and safety",
  safe_zone_repair_variation: "Rendering and safety",
  image_edit_916_text_safe_zone: "Image editing",
  image_edit_verified_masked_text: "Image editing",
  image_edit_copy_delivery_repair: "Image editing",
  ideogram_text_edit: "Image editing",
  ad_delayer_layered_edit: "Image editing",
  image_edit_selected_text_editing: "Image editing",
  adaptive_text_edit_layout: "Image editing",
  reflow_safe_text_paste: "Image editing",
  region_masked_image_edit: "Image editing",
  fast_image_edit_pipeline: "Image editing",
  image_edit_speed_boost: "Image editing",
  region_edit_grouping: "Image editing",
  element_aware_text_edit: "Image editing",
  edit_reference_images: "Image editing",
  image_edit_quality_guards: "Image editing",
  strict_reposition_reroute: "Image editing",
  confined_text_edit_paste: "Image editing",
  region_edit_quick_actions: "Image editing",
  masked_edit_goal_picker: "Image editing",
  erase_smudge_guards: "Image editing",
  erase_target_attribution: "Image editing",
  erase_hallucination_check: "Image editing",
  erase_mask_interior_core: "Image editing",
  erase_band_local_repair: "Image editing",
  restore_original_any_edit: "Image editing",
  creative_editor: "Image editing",
  ad_notes: "Collaboration",
};

const IMAGE_ENGINE_OPTIONS: { value: string; label: string; detail: string }[] = [
  {
    value: "default",
    label: "Default (recommended)",
    detail:
      "9:16 renders its 4:5 base on Nano Banana 2.1 / 2.0 (global setting), then expands (other ratios always use default routing)",
  },
  {
    value: "gpt-image-2",
    label: "GPT-Image-2 (test)",
    detail: "9:16 only: the 4:5 base of 9:16 renders on GPT-Image-2",
  },
  {
    value: "flux-2-pro",
    label: "Flux 2 Pro (test)",
    detail: "9:16 only: the 4:5 base of 9:16 renders on FLUX.2 [pro] via fal",
  },
];

function BrandImageEngineControl({ brand }: { brand: Brand }) {
  const { toast } = useToast();
  const { data: engineData, isLoading } = useQuery<{ overrides: ImageEngineOverrideRow[] }>({
    queryKey: ["/api/admin/ads-express-image-engines"],
  });
  const current =
    (engineData?.overrides ?? []).find((override) => override.brandId === brand.id)?.engine ??
    "default";
  const setEngineMutation = useMutation({
    mutationFn: async ({ engine }: { engine: string }) => {
      await apiRequest("PUT", `/api/admin/brands/${brand.id}/image-engine`, { engine });
    },
    onSuccess: (_data, variables) => {
      toast({
        title:
          variables.engine === "default"
            ? "Image engine reset to Default"
            : `Image engine set to ${
                IMAGE_ENGINE_OPTIONS.find((option) => option.value === variables.engine)?.label ??
                variables.engine
              }`,
        description: `${brand.name} — applies to new Ads Express runs from their next Generate.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to set image engine",
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ads-express-image-engines"] });
    },
  });

  return (
    <div className="rounded-md border p-4 space-y-2" data-testid="card-image-engine-override">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium">
            Image engine (test override)
            {current !== "default" && (
              <span className="ml-2 font-mono text-[11px] text-amber-600 dark:text-amber-500">
                override active
              </span>
            )}
          </h3>
          <p className="text-xs text-muted-foreground">
            Forces the 4:5 base of this brand&apos;s 9:16 Ads Express renders onto one engine for
            side-by-side testing. Other ratios use Default routing. Applies at Generate time; Kill
            Crew is unaffected.
          </p>
        </div>
        <Select
          value={isLoading ? "" : current}
          disabled={isLoading || setEngineMutation.isPending}
          onValueChange={(engine) => setEngineMutation.mutate({ engine })}
        >
          <SelectTrigger className="sm:w-[260px] shrink-0" data-testid="select-image-engine">
            <SelectValue placeholder="Loading…" />
          </SelectTrigger>
          <SelectContent>
            {IMAGE_ENGINE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex flex-col items-start">
                  <span>{option.label}</span>
                  <span className="text-[11px] text-muted-foreground max-w-[340px] whitespace-normal text-left">
                    {option.detail}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function BrandPromptGeneratorControl({ brand }: { brand: Brand }) {
  const { toast } = useToast();
  const [url, setUrl] = useState("");
  const { data, isLoading } = useQuery<{
    brandId: string;
    promptGeneratorSimUrl: string | null;
    isDefault: boolean;
    updatedAt: string | null;
    updatedByName: string | null;
  }>({
    queryKey: ["/api/admin/brands", brand.id, "ads-express-workflow-config"],
    queryFn: async () => {
      const response = await fetch(`/api/admin/brands/${brand.id}/ads-express-workflow-config`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to load prompt generator config");
      return response.json();
    },
  });

  useEffect(() => {
    setUrl(data?.promptGeneratorSimUrl ?? "");
  }, [data?.promptGeneratorSimUrl, brand.id]);

  const save = useMutation({
    mutationFn: async () =>
      apiRequest("PUT", `/api/admin/brands/${brand.id}/ads-express-workflow-config`, {
        promptGeneratorSimUrl: url,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/brands", brand.id, "ads-express-workflow-config"],
      });
      toast({ title: "Prompt generator saved", description: `${brand.name} uses this workflow for new Ads Express runs.` });
    },
    onError: (error: Error) =>
      toast({ title: "Save failed", description: error.message, variant: "destructive" }),
  });
  const clear = useMutation({
    mutationFn: async () =>
      apiRequest("DELETE", `/api/admin/brands/${brand.id}/ads-express-workflow-config`),
    onSuccess: () => {
      setUrl("");
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/brands", brand.id, "ads-express-workflow-config"],
      });
      toast({ title: "Prompt generator reset", description: "The standard Ads Express workflow is active." });
    },
    onError: (error: Error) =>
      toast({ title: "Clear failed", description: error.message, variant: "destructive" }),
  });

  const isDefault = !data?.promptGeneratorSimUrl;
  return (
    <div className="rounded-md border p-4 space-y-3" data-testid="card-ads-express-prompt-generator">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-medium">Advanced prompt generator</div>
        {isDefault ? (
          <Badge variant="secondary" data-testid="badge-ads-express-prompt-generator-default">Default</Badge>
        ) : (
          <Badge variant="outline">Brand override</Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Optional Sim workflow URL for this brand&apos;s standard Ads Express prompt generation.
        Leave blank to use the default URL pair and routing.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://www.sim.ai/api/v2/workflows/.../execute"
          disabled={isLoading || save.isPending || clear.isPending}
          data-testid="input-ads-express-prompt-generator-url"
        />
        <Button
          onClick={() => save.mutate()}
          disabled={isLoading || save.isPending || clear.isPending}
          data-testid="button-save-ads-express-prompt-generator"
        >
          {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save
        </Button>
        <Button
          variant="outline"
          onClick={() => clear.mutate()}
          disabled={isLoading || save.isPending || clear.isPending || isDefault}
          data-testid="button-clear-ads-express-prompt-generator"
        >
          Clear
        </Button>
      </div>
      {!isDefault && data?.updatedByName && (
        <div className="text-[11px] text-muted-foreground">
          Last updated by {data.updatedByName}
        </div>
      )}
    </div>
  );
}

export function AdsExpressConfigTab({ allBrands }: { allBrands: Brand[] }) {
  const { toast } = useToast();
  const activeBrands = allBrands.filter((brand) => brand.status !== "archived");
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [featureSearch, setFeatureSearch] = useState("");
  const [brandSearch, setBrandSearch] = useState("");
  const [brandViewSearch, setBrandViewSearch] = useState("");
  const [expandedBrand, setExpandedBrand] = useState<Set<string>>(new Set());
  const [defaultBrandOpen, setDefaultBrandOpen] = useState(false);
  const [brandFilter, setBrandFilter] = useState("all");
  const [defaultGroupOpen, setDefaultGroupOpen] = useState(false);
  const [confirmDisableKey, setConfirmDisableKey] = useState<string | null>(null);
  const selectedBrand = activeBrands.find((brand) => brand.id === selectedBrandId) ?? null;

  const { data: brandSettings, isLoading: brandSettingsLoading } =
    useQuery<BrandSettingsResponse>({
      queryKey: ["/api/admin/ads-express-brand-settings"],
    });
  // Evidence remains registered/stored in the legacy API, but is administered
  // in Persona Workflows. Filter before deriving lists, counts or bulk controls.
  const definitions = (brandSettings?.settings ?? []).filter(
    (definition) => definition.key !== CUSTOMER_EVIDENCE_FLAG,
  );
  const explicitValues = brandSettings?.values ?? [];
  const settingEnabled = (brandId: string, definition: BrandSettingDefinition) =>
    explicitValues.find(
      (value) => value.brandId === brandId && value.settingKey === definition.key,
    )?.enabled ?? definition.defaultEnabled;
  const invalidateBrandSettings = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/ads-express-brand-settings"] });
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.includes("customer-evidence"),
    });
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.some(
          (part) =>
            typeof part === "string" &&
            (part === "beta-features" || part.endsWith("/beta-features")),
        ),
    });
  };
  const setBrandSetting = useMutation({
    mutationFn: async ({
      brandId,
      settingKey,
      enabled,
    }: {
      brandId: string;
      settingKey: string;
      enabled: boolean;
    }) =>
      apiRequest("PUT", `/api/admin/brands/${brandId}/ads-express-settings`, {
        settingKey,
        enabled,
      }),
    onSuccess: () => {
      invalidateBrandSettings();
      toast({ title: "Brand setting saved" });
    },
    onError: (error: Error) =>
      toast({ title: "Save failed", description: error.message, variant: "destructive" }),
  });
  const setBulkSetting = useMutation({
    mutationFn: async ({
      settingKey,
      enabled,
    }: {
      settingKey: string;
      enabled: boolean;
    }) =>
      apiRequest("PUT", "/api/admin/ads-express-brand-settings/bulk", {
        brandIds: activeBrands.map((brand) => brand.id),
        settingKeys: [settingKey],
        enabled,
      }),
    onSuccess: () => {
      invalidateBrandSettings();
      toast({ title: "All brand settings updated" });
    },
    onError: (error: Error) =>
      toast({ title: "Bulk update failed", description: error.message, variant: "destructive" }),
  });
  const matchingDefinitions = useMemo(() => {
    const query = featureSearch.trim().toLowerCase();
    if (!query) return definitions;
    return definitions.filter((definition) =>
      `${definition.name} ${definition.key} ${definition.description}`
        .toLowerCase()
        .includes(query),
    );
  }, [definitions, featureSearch]);
  const definitionGroups = GROUP_ORDER.map((name) => ({
    name,
    values: matchingDefinitions.filter(
      (definition) => (!definition.defaultEnabled || ["safe_zone_person_seam_guard", "safe_zone_direct_916_guard"].includes(definition.key)) && (FEATURE_GROUPS[definition.key] ?? "Other") === name,
    ),
  })).filter((group) => group.values.length);
  const defaultOnDefinitions = matchingDefinitions.filter((definition) => definition.defaultEnabled && !["safe_zone_person_seam_guard", "safe_zone_direct_916_guard"].includes(definition.key));
  const brandVisibleDefinitions = definitions.filter((definition) =>
    `${definition.name} ${definition.key} ${definition.description}`
      .toLowerCase()
      .includes(brandViewSearch.trim().toLowerCase()),
  );
  const brandDefinitionGroups = GROUP_ORDER.map((name) => ({
    name,
    values: brandVisibleDefinitions.filter(
      (definition) => (!definition.defaultEnabled || ["safe_zone_person_seam_guard", "safe_zone_direct_916_guard"].includes(definition.key)) && (FEATURE_GROUPS[definition.key] ?? "Other") === name,
    ),
  })).filter((group) => group.values.length);
  const brandDefaultDefinitions = brandVisibleDefinitions.filter((definition) => definition.defaultEnabled && !["safe_zone_person_seam_guard", "safe_zone_direct_916_guard"].includes(definition.key));

  const { data: globalSettings, isLoading: globalLoading } = useQuery<{
    enhancedPipeline: boolean;
    recentGenerationsPanel?: boolean;
    runQueue?: boolean;
    portraitReframeEnabled?: boolean;
  }>({ queryKey: ["/api/admin/ads-express-settings"] });

  const savePortraitReframe = useMutation({
    mutationFn: async (portraitReframeEnabled: boolean) => {
      const response = await apiRequest("PUT", "/api/admin/ads-express-settings", { portraitReframeEnabled });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ads-express-settings"] });
      toast({ title: "Saved", description: "Two-pass portraits setting saved. Existing jobs keep their saved plan." });
    },
    onError: (error: Error) => {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    },
  });

  const saveRecentPanel = useMutation({
    mutationFn: async (recentGenerationsPanel: boolean) => {
      const response = await apiRequest("PUT", "/api/admin/ads-express-settings", {
        recentGenerationsPanel,
      });
      return response.json();
    },
    onSuccess: (data: { recentGenerationsPanel: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ads-express-settings"] });
      toast({
        title: "Saved",
        description: data.recentGenerationsPanel
          ? "Recent generations panel is on for master admins."
          : "Recent generations panel is off — master admins see the standard Previous Sessions panel.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    },
  });

  const saveRunQueue = useMutation({
    mutationFn: async (runQueue: boolean) => {
      const response = await apiRequest("PUT", "/api/admin/ads-express-settings", { runQueue });
      return response.json();
    },
    onSuccess: (data: { runQueue: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/ads-express-settings"] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.some(
            (part) =>
              typeof part === "string" &&
              (part === "beta-features" || part.endsWith("/beta-features")),
          ),
      });
      toast({
        title: "Saved",
        description: data.runQueue
          ? "Run queue is on — master admins can queue Ads Express runs."
          : "Run queue is off — everyone gets the single-run behavior.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="text-2xl font-semibold" data-testid="text-ads-express-config-title">
          Ads Express features
        </h2>
        <p className="text-sm text-muted-foreground">
          Turn a feature on for every brand at once, or manage brands individually below.
        </p>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium">Global switches</h3>
        <div className="rounded-md border divide-y">
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="space-y-0.5">
              <div className="text-sm font-medium">Recent generations panel</div>
              <div className="text-xs text-muted-foreground">
                Master admins see the enriched Recent generations table instead of Previous
                Sessions.
              </div>
            </div>
            <Switch
              checked={globalSettings?.recentGenerationsPanel === true}
              disabled={globalLoading || saveRecentPanel.isPending}
              onCheckedChange={(checked) => saveRecentPanel.mutate(checked)}
              data-testid="switch-ads-express-recent-generations-panel-global"
            />
          </div>
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="space-y-0.5">
              <div className="text-sm font-medium">Run queue</div>
              <div className="text-xs text-muted-foreground">
                Master admins can queue additional runs while one is already in progress.
              </div>
            </div>
            <Switch
              checked={globalSettings?.runQueue === true}
              disabled={globalLoading || saveRunQueue.isPending}
              onCheckedChange={(checked) => saveRunQueue.mutate(checked)}
              data-testid="switch-ads-express-run-queue-global"
            />
          </div>
          <NanoBananaVersionControl />
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="space-y-0.5">
              <div className="text-sm font-medium">Two-pass 9:16 portraits</div>
              <div className="text-xs text-muted-foreground">
                Standard static Ads Express only. Off overrides every brand setting for new jobs and fresh regenerations.
              </div>
            </div>
            <Switch
              checked={globalSettings?.portraitReframeEnabled !== false}
              disabled={globalLoading || savePortraitReframe.isPending}
              onCheckedChange={(checked) => savePortraitReframe.mutate(checked)}
              data-testid="switch-ads-express-portrait-reframe-global"
            />
          </div>
        </div>
      </div>
      <AdsExpressNativeInstructions />

      {!selectedBrand ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">All-brand feature overview</CardTitle>
              <CardDescription>
                These brand settings do not add or remove anyone&apos;s global Beta Features grants.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Find a feature"
                  value={featureSearch}
                  onChange={(event) => setFeatureSearch(event.target.value)}
                  data-testid="input-search-features"
                />
              </div>
              {brandSettingsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading brand settings…
                </div>
              ) : (
                <>
                {definitionGroups.map((group) => (
                  <div key={group.name} className="space-y-2">
                    <h3 className="text-sm font-medium">{group.name}</h3>
                    <div className="divide-y rounded-md border">
                      {group.values.map((definition) => {
                        const enabledCount = activeBrands.filter((brand) =>
                          settingEnabled(brand.id, definition),
                        ).length;
                        return (
                          <div
                            key={definition.key}
                            className="flex items-center gap-3 p-3"
                            data-testid={`row-feature-${definition.key}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium">{definition.name}</span>
                                {definition.requiresUserGrant && (
                                  <Badge variant="secondary" className="text-[10px]">
                                    also requires user beta grant
                                  </Badge>
                                )}
                              </div>
                              <p className="truncate text-xs text-muted-foreground">
                                {definition.description}
                              </p>
                            </div>
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {enabledCount} of {activeBrands.length} on
                            </span>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  disabled={setBulkSetting.isPending || !activeBrands.length}
                                  data-testid={`button-feature-menu-${definition.key}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => setBulkSetting.mutate({
                                    settingKey: definition.key,
                                    enabled: true,
                                  })}
                                  data-testid={`button-enable-all-${definition.key}`}
                                >
                                  Enable for all brands
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => setConfirmDisableKey(definition.key)}
                                  data-testid={`button-disable-all-${definition.key}`}
                                >
                                  Disable for all brands
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {defaultOnDefinitions.length > 0 && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 py-1 text-left"
                      onClick={() => setDefaultGroupOpen((open) => !open)}
                      aria-expanded={defaultGroupOpen}
                      data-testid="button-toggle-default-on-group-global"
                    >
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${defaultGroupOpen ? "" : "-rotate-90"}`} />
                      <h3 className="text-sm font-medium">Default on</h3>
                      <Badge variant="secondary" data-testid="badge-default-on-count-global">
                        {defaultOnDefinitions.length}
                      </Badge>
                    </button>
                    {defaultGroupOpen && (
                      <div className="divide-y rounded-md border" data-testid="group-default-on-global">
                        {defaultOnDefinitions.map((definition) => (
                          <div key={definition.key} className="flex items-center gap-3 p-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium">{definition.name}</p>
                              <p className="truncate text-xs text-muted-foreground">{definition.description}</p>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {activeBrands.filter((brand) => settingEnabled(brand.id, definition)).length} of {activeBrands.length} on
                            </span>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  disabled={setBulkSetting.isPending || !activeBrands.length}
                                  data-testid={`button-feature-menu-${definition.key}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => setBulkSetting.mutate({
                                    settingKey: definition.key,
                                    enabled: true,
                                  })}
                                  data-testid={`button-enable-all-${definition.key}`}
                                >
                                  Enable for all brands
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => setConfirmDisableKey(definition.key)}
                                  data-testid={`button-disable-all-${definition.key}`}
                                >
                                  Disable for all brands
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                </>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Per-brand settings</CardTitle>
              <CardDescription>Select a brand to inspect and change its rollout.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Find a brand"
                  value={brandSearch}
                  onChange={(event) => setBrandSearch(event.target.value)}
                  data-testid="input-search-brands"
                />
              </div>
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="w-full sm:w-[190px]" data-testid="select-brand-feature-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All brands</SelectItem>
                  <SelectItem value="all-on">All features on</SelectItem>
                  <SelectItem value="partial">Partially enabled</SelectItem>
                  <SelectItem value="off">All features off</SelectItem>
                </SelectContent>
              </Select>
              <div className="divide-y rounded-md border">
                {activeBrands
                  .filter((brand) =>
                    brand.name.toLowerCase().includes(brandSearch.trim().toLowerCase()),
                  )
                  .filter((brand) => {
                    const count = definitions.filter((definition) => settingEnabled(brand.id, definition)).length;
                    if (brandFilter === "all-on") return count === definitions.length;
                    if (brandFilter === "partial") return count > 0 && count < definitions.length;
                    if (brandFilter === "off") return count === 0;
                    return true;
                  })
                  .map((brand) => {
                    const enabledCount = definitions.filter((definition) =>
                      settingEnabled(brand.id, definition),
                    ).length;
                    return (
                      <button
                        type="button"
                        key={brand.id}
                        className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/50"
                        onClick={() => {
                          setSelectedBrandId(brand.id);
                          setBrandViewSearch("");
                          setExpandedBrand(new Set());
                          setDefaultBrandOpen(false);
                        }}
                        data-testid={`button-select-brand-${brand.id}`}
                      >
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="flex-1 truncate text-sm font-medium">{brand.name}</span>
                        <span className="hidden items-center gap-1 sm:flex" data-testid={`feature-dots-${brand.id}`}>
                          {definitions.map((definition) => (
                            <span
                              key={definition.key}
                              className={`h-2 w-2 rounded-full ${
                                settingEnabled(brand.id, definition) ? "bg-emerald-500" : "bg-muted-foreground/25"
                              }`}
                              title={`${definition.name}: ${settingEnabled(brand.id, definition) ? "on" : "off"}`}
                              aria-label={`${definition.name}: ${settingEnabled(brand.id, definition) ? "on" : "off"}`}
                            />
                          ))}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {enabledCount} of {definitions.length} on
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </button>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <Button
              variant="ghost"
              size="sm"
              className="w-fit -ml-2"
              onClick={() => setSelectedBrandId(null)}
              data-testid="button-back-to-features"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> All brands
            </Button>
            <CardTitle>{selectedBrand.name}</CardTitle>
            <CardDescription>
              Brand rollout settings. Experimental rows also require the user&apos;s global grant.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <Select value={selectedBrand.id} onValueChange={setSelectedBrandId}>
              <SelectTrigger className="w-full sm:w-[320px]" data-testid="select-config-brand">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {activeBrands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <BrandImageEngineControl brand={selectedBrand} />
            <BrandPromptGeneratorControl brand={selectedBrand} />
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Find a feature"
                value={brandViewSearch}
                onChange={(event) => setBrandViewSearch(event.target.value)}
                data-testid="input-search-brand-features"
              />
            </div>
            {brandDefinitionGroups.map((group) => (
              <div key={group.name} className="space-y-2">
                <h3 className="text-sm font-medium">{group.name}</h3>
                <div className="divide-y rounded-md border">
                  {group.values.map((definition) => (
                    <div key={definition.key} className="flex items-center gap-4 p-3">
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          className="flex items-center gap-1 text-left text-sm font-medium"
                          onClick={() => {
                            const next = new Set(expandedBrand);
                            next.has(definition.key) ? next.delete(definition.key) : next.add(definition.key);
                            setExpandedBrand(next);
                          }}
                          data-testid={`button-expand-brand-feature-${definition.key}`}
                        >
                          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expandedBrand.has(definition.key) ? "rotate-90" : ""}`} />
                          {definition.name}
                        </button>
                        {expandedBrand.has(definition.key) && (
                          <p className="mt-1 text-xs text-muted-foreground">{definition.description}</p>
                        )}
                      </div>
                      <Switch
                        checked={settingEnabled(selectedBrand.id, definition)}
                        disabled={setBrandSetting.isPending}
                        onCheckedChange={(enabled) =>
                          setBrandSetting.mutate({
                            brandId: selectedBrand.id,
                            settingKey: definition.key,
                            enabled,
                          })
                        }
                        data-testid={`toggle-${definition.key}-${selectedBrand.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {brandDefaultDefinitions.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  className="flex items-center gap-2"
                  onClick={() => setDefaultBrandOpen((open) => !open)}
                  data-testid="button-toggle-default-on-group-brand"
                >
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${defaultBrandOpen ? "" : "-rotate-90"}`} />
                  <span className="text-sm font-medium">Default on</span>
                  <Badge variant="secondary" data-testid="badge-default-on-count-brand">
                    {brandDefaultDefinitions.length}
                  </Badge>
                </button>
                {defaultBrandOpen && (
                  <div className="divide-y rounded-md border" data-testid="group-default-on-brand">
                    {brandDefaultDefinitions.map((definition) => (
                      <div key={definition.key} className="flex items-center gap-4 p-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{definition.name}</p>
                          <p className="text-xs text-muted-foreground">{definition.description}</p>
                        </div>
                        <Switch
                          checked={settingEnabled(selectedBrand.id, definition)}
                          disabled={setBrandSetting.isPending}
                          onCheckedChange={(enabled) => setBrandSetting.mutate({
                            brandId: selectedBrand.id,
                            settingKey: definition.key,
                            enabled,
                          })}
                          data-testid={`toggle-${definition.key}-${selectedBrand.id}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <AlertDialog
        open={confirmDisableKey !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDisableKey(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable for every brand?</AlertDialogTitle>
            <AlertDialogDescription>
              This turns the brand setting off everywhere. It does not change user grants in Admin → Beta Features.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDisableKey) {
                  setBulkSetting.mutate({ settingKey: confirmDisableKey, enabled: false });
                }
                setConfirmDisableKey(null);
              }}
              data-testid="button-confirm-disable-all"
            >
              Disable for all brands
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
